"use client";

import { SearchIcon } from "@chakra-ui/icons";
import { Box, Flex, Input, Skeleton } from "@chakra-ui/react";
import type { KeyboardEventHandler } from "react";
import { HiOutlineXMark } from "react-icons/hi2";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { useThemeTokens } from "@/lib/theme/tokens";

export type QuickNameSearchProps = {
  firstNameDraft: string;
  firstNameCommitted: string;
  lastNameDraft: string;
  lastNameCommitted: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onCommit: () => void;
  onClear: () => void;
  hasUnapplied?: boolean;
  "data-testid"?: string;
};

export function QuickNameSearch({
  firstNameDraft,
  firstNameCommitted,
  lastNameDraft,
  lastNameCommitted,
  onFirstNameChange,
  onLastNameChange,
  onKeyDown,
  onCommit,
  onClear,
  hasUnapplied = false,
  "data-testid": dataTestId,
}: QuickNameSearchProps) {
  const theme = useThemeTokens();

  const canSearch =
    firstNameDraft.trim().length > 0 || lastNameDraft.trim().length > 0;
  const showClear =
    firstNameDraft.length > 0 ||
    lastNameDraft.length > 0 ||
    firstNameCommitted.trim().length > 0 ||
    lastNameCommitted.trim().length > 0;
  const searchVariant =
    canSearch && hasUnapplied ? "primary" : "outline";

  return (
    <Flex
      h="40px"
      w="100%"
      minW={{ md: "320px" }}
      maxW="440px"
      align="center"
      gap={2}
      pl={2.5}
      pr={1}
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      borderRadius="8px"
      bg="transparent"
      transition="all 0.18s ease-out"
      data-testid={dataTestId}
      _focusWithin={{
        borderColor: theme.accent.primaryBorder,
        boxShadow: `0 0 0 1px ${theme.accent.primaryBorder}`,
      }}
    >
      <SearchIcon
        color={theme.text.secondary}
        w="14px"
        h="14px"
        flexShrink={0}
        aria-hidden
      />

      <Flex flex={1} minW={0} align="center">
        <Input
          flex={1}
          minW={0}
          h="32px"
          variant="unstyled"
          px={2}
          value={firstNameDraft}
          onChange={(event) => onFirstNameChange(event.target.value)}
          onKeyDown={onKeyDown}
          data-testid={
            dataTestId ? `${dataTestId}-first-name` : undefined
          }
          placeholder="First name"
          fontSize="xs"
          fontWeight="500"
          color={theme.text.primary}
          _placeholder={{ color: theme.text.secondary, fontSize: "xs" }}
        />

        <Box
          alignSelf="stretch"
          w="1px"
          my={1.5}
          bg={theme.borderGlass.default}
          flexShrink={0}
          aria-hidden
        />

        <Input
          flex={1}
          minW={0}
          h="32px"
          variant="unstyled"
          px={2}
          value={lastNameDraft}
          onChange={(event) => onLastNameChange(event.target.value)}
          onKeyDown={onKeyDown}
          data-testid={
            dataTestId ? `${dataTestId}-last-name` : undefined
          }
          placeholder="Last name"
          fontSize="xs"
          fontWeight="500"
          color={theme.text.primary}
          _placeholder={{ color: theme.text.secondary, fontSize: "xs" }}
        />
      </Flex>

      <Flex align="center" gap={0.5} flexShrink={0} w="96px" justify="flex-end">
        <IconButton
          aria-label="Clear name search"
          icon={<HiOutlineXMark size={15} />}
          size="sm"
          variant="ghost"
          minW="28px"
          w="28px"
          h="28px"
          visibility={showClear ? "visible" : "hidden"}
          pointerEvents={showClear ? "auto" : "none"}
          tabIndex={showClear ? 0 : -1}
          onClick={onClear}
          data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
        />

        <Button
          h="28px"
          minW="64px"
          px={2.5}
          fontSize="xs"
          fontWeight="600"
          variant={searchVariant}
          onClick={onCommit}
          isDisabled={!canSearch}
          aria-label="Run name search"
          data-testid={dataTestId ? `${dataTestId}-submit` : undefined}
        >
          Search
        </Button>
      </Flex>
    </Flex>
  );
}

export function QuickNameSearchSkeleton({
  "data-testid": dataTestId,
}: {
  "data-testid"?: string;
}) {
  const theme = useThemeTokens();

  return (
    <Flex
      h="40px"
      w="100%"
      minW={{ md: "320px" }}
      maxW="440px"
      align="center"
      gap={2}
      pl={2.5}
      pr={1}
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      borderRadius="8px"
      data-testid={dataTestId}
    >
      <Skeleton w="14px" h="14px" borderRadius="full" flexShrink={0} />
      <Flex flex={1} gap={2} px={1}>
        <Skeleton flex={1} h="12px" borderRadius="4px" />
        <Skeleton flex={1} h="12px" borderRadius="4px" />
      </Flex>
      <Skeleton w="96px" h="28px" borderRadius="6px" flexShrink={0} />
    </Flex>
  );
}
